import { Component } from './component.js';
import { onDocumentLoaded, changeMetaThemeColor } from './utilities.js';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      const isAtTop = this.getBoundingClientRect().top >= 0;

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (this.getBoundingClientRect().top >= 0) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});



if (typeof debounce > "u")
    var debounce = function(fn, wait) {
        let t;
        return (...args) => {
            clearTimeout(t),
            t = setTimeout( () => fn.apply(this, args), wait)
        }
    };
var dispatchCustomEvent = function(eventName) {
    var data = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {}
      , detail = {
        detail: data
    }
      , event = new CustomEvent(eventName,data ? detail : null);
    document.dispatchEvent(event)
};
if (window.recentlyViewedIds = [],
!customElements.get("quantity-selector")) {
    class QuantityInput extends HTMLElement {
        constructor() {
            super(),
            this.input = this.querySelector(".qty"),
            this.step = this.input.getAttribute("step"),
            this.changeEvent = new Event("change",{
                bubbles: !0
            }),
            this.subtract = this.querySelector(".minus"),
            this.add = this.querySelector(".plus"),
            this.subtract.addEventListener("click", () => this.change_quantity(-1 * this.step)),
            this.add.addEventListener("click", () => this.change_quantity(1 * this.step)),
            this.validateQtyRules()
        }
        connectedCallback() {
            this.classList.add("buttons_added")
        }
        change_quantity(change) {
            let quantity = Number(this.input.value);
            isNaN(quantity) && (quantity = 1),
            !(this.input.getAttribute("min") > quantity + change) && (this.input.getAttribute("max") && this.input.getAttribute("max") < quantity + change || (quantity += change,
            quantity = Math.max(quantity, 1),
            this.input.value = quantity,
            this.input.dispatchEvent(this.changeEvent),
            this.validateQtyRules()))
        }
        validateQtyRules() {
            const value = parseInt(this.input.value);
            if (this.input.min) {
                const min = parseInt(this.input.min);
                this.subtract.classList.toggle("disabled", value <= min)
            }
            if (this.input.max) {
                const max = parseInt(this.input.max);
                this.add.classList.toggle("disabled", value >= max)
            }
        }
    }
    customElements.define("quantity-selector", QuantityInput)
}
class ArrowSubMenu {
    constructor(self) {
        this.submenu = self.parentNode.querySelector(".sub-menu"),
        this.arrow = self,
        self.addEventListener("click", e => this.toggle_submenu(e))
    }
    toggle_submenu(e) {
        e.preventDefault();
        let submenu = this.submenu;
        submenu.classList.contains("active") ? (submenu.classList.remove("active"),
        this.arrow.blur()) : submenu.classList.add("active")
    }
}
let arrows = document.querySelectorAll(".thb-arrow");
if (arrows.forEach(arrow => {
    new ArrowSubMenu(arrow)
}
),
!customElements.get("product-card")) {
    class ProductCard extends HTMLElement {
        constructor() {
            super(),
            this.swatches = this.querySelector(".product-card-swatches"),
            this.image = this.querySelector(".product-featured-image-link .product-primary-image"),
            this.additional_images = this.querySelectorAll(".product-secondary-image"),
            this.additional_images_nav = this.querySelectorAll(".product-secondary-images-nav li"),
            this.quick_add = this.querySelector(".product-card--add-to-cart-button-simple")
        }
        connectedCallback() {
            this.swatches && this.enableSwatches(this.swatches, this.image),
            this.additional_images && this.enableAdditionalImages(),
            this.quick_add && this.enableQuickAdd()
        }
        enableAdditionalImages() {
            let image_length = this.additional_images.length
              , images = this.additional_images
              , nav = this.additional_images_nav
              , image_container = this.querySelector(".product-featured-image");
            const mousemove = function(e) {
                let l = e.offsetX
                  , w = this.getBoundingClientRect().width
                  , prc = l / w
                  , sel = Math.floor(prc * image_length)
                  , selimg = images[sel];
                images.forEach( (image, index) => {
                    image.classList.contains("hover") && (image.classList.remove("hover"),
                    nav.length && nav[index].classList.remove("active"))
                }
                ),
                selimg && (selimg.classList.contains("hover") || (selimg.classList.add("hover"),
                nav.length && nav[sel].classList.add("active")))
            }
              , mouseleave = function(e) {
                images.forEach( (image, index) => {
                    image.classList.remove("hover"),
                    nav.length && nav[index].classList.remove("active")
                }
                )
            };
            image_container && (image_container.addEventListener("touchstart", mousemove, {
                passive: !0
            }),
            image_container.addEventListener("touchmove", mousemove, {
                passive: !0
            }),
            image_container.addEventListener("touchend", mouseleave, {
                passive: !0
            }),
            image_container.addEventListener("mouseenter", mousemove, {
                passive: !0
            }),
            image_container.addEventListener("mousemove", mousemove, {
                passive: !0
            }),
            image_container.addEventListener("mouseleave", mouseleave, {
                passive: !0
            })),
            images.forEach(function(image) {
                window.addEventListener("load", event => {
                    lazySizes.loader.unveil(image)
                }
                )
            })
        }
        enableSwatches(swatches, image) {
            let swatch_list = swatches.querySelectorAll(".product-card-swatch")
              , org_srcset = image ? image.dataset.srcset : "";
            swatch_list.forEach( (swatch, index) => {
                window.addEventListener("load", event => {
                    let image2 = new Image;
                    image2.srcset = swatch.dataset.srcset,
                    lazySizes.loader.unveil(image2)
                }
                ),
                swatch.addEventListener("mouseover", function() {
                    [].forEach.call(swatch_list, function(el) {
                        el.classList.remove("active")
                    }),
                    image && (swatch.dataset.srcset ? image.setAttribute("srcset", swatch.dataset.srcset) : image.setAttribute("srcset", org_srcset)),
                    swatch.classList.add("active")
                }),
                swatch.addEventListener("click", function(evt) {
                    window.location.href = this.dataset.href,
                    evt.preventDefault()
                })
            }
            )
        }
        enableQuickAdd() {
            this.quick_add.addEventListener("click", this.quickAdd.bind(this))
        }
        quickAdd(evt) {
            if (evt.preventDefault(),
            this.quick_add.disabled)
                return;
            this.quick_add.classList.add("loading"),
            this.quick_add.setAttribute("aria-disabled", !0);
            const config = {
                method: "POST",
                headers: {
                    "X-Requested-With": "XMLHttpRequest",
                    Accept: "application/javascript"
                }
            };
            let formData = new FormData(this.form);
            return formData.append("id", this.quick_add.dataset.productId),
            formData.append("quantity", 1),
            formData.append("sections", this.getSectionsToRender().map(section => section.section)),
            formData.append("sections_url", window.location.pathname),
            config.body = formData,
            fetch(`${theme.routes.cart_add_url}`, config).then(response => response.json()).then(response => {
                response.status || (this.renderContents(response),
                dispatchCustomEvent("cart:item-added", {
                    product: response.hasOwnProperty("items") ? response.items[0] : response
                }))
            }
            ).catch(e => {
                console.error(e)
            }
            ).finally( () => {
                this.quick_add.classList.remove("loading"),
                this.quick_add.removeAttribute("aria-disabled")
            }
            ),
            !1
        }
        getSectionsToRender() {
            return [{
                id: "Cart",
                section: "main-cart",
                selector: ".thb-cart-form"
            }, {
                id: "Cart-Drawer",
                section: "cart-drawer",
                selector: ".cart-drawer"
            }, {
                id: "cart-drawer-toggle",
                section: "cart-bubble",
                selector: ".thb-item-count"
            }]
        }
        renderContents(parsedState) {
            this.getSectionsToRender().forEach(section => {
                if (!document.getElementById(section.id))
                    return;
                const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
                elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector),
                typeof Cart < "u" && new Cart().renderContents(parsedState),
                section.id === "Cart-Drawer" && document.getElementById("Cart-Drawer")?.removeProductEvent()
            }
            ),
            document.getElementById("Cart-Drawer") && document.getElementById("Cart-Drawer").open()
        }
        getSectionInnerHTML(html, selector=".shopify-section") {
            return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML
        }
    }
    customElements.define("product-card", ProductCard)
}
if (!customElements.get("product-card-small")) {
    class ProductCardSmall extends HTMLElement {
        constructor() {
            super(),
            this.quick_add_enabled = this.classList.contains("quick-add-to-card--true"),
            this.button = this.querySelector("button"),
            this.id = this.dataset.id,
            this.url = this.dataset.url
        }
        connectedCallback() {
            this.quick_add_enabled && this.setEventListeners()
        }
        setEventListeners() {
            this.button.addEventListener("click", this.addToCart.bind(this))
        }
        getSectionsToRender() {
            return [{
                id: "Cart",
                section: "main-cart",
                selector: ".thb-cart-form"
            }, {
                id: "Cart-Drawer",
                section: "cart-drawer",
                selector: ".cart-drawer"
            }, {
                id: "cart-drawer-toggle",
                section: "cart-bubble",
                selector: ".thb-item-count"
            }]
        }
        addToCart() {
            this.button.classList.add("loading"),
            this.quick_add_enabled,
            this.button.setAttribute("aria-disabled", !0);
            const config = {
                method: "POST",
                headers: {
                    "X-Requested-With": "XMLHttpRequest",
                    Accept: "application/javascript"
                }
            };
            let formData = new FormData(this.form);
            formData.append("id", this.id),
            formData.append("quantity", 1),
            formData.append("sections", this.getSectionsToRender().map(section => section.section)),
            formData.append("sections_url", window.location.pathname),
            config.body = formData,
            fetch(`${theme.routes.cart_add_url}`, config).then(response => response.json()).then(response => {
                response.status || (this.renderContents(response),
                dispatchCustomEvent("cart:item-added", {
                    product: response.hasOwnProperty("items") ? response.items[0] : response
                }))
            }
            ).catch(e => {
                console.error(e)
            }
            ).finally( () => {
                this.button.classList.remove("loading"),
                this.button.removeAttribute("aria-disabled")
            }
            )
        }
        renderContents(parsedState) {
            this.getSectionsToRender().forEach(section => {
                if (!document.getElementById(section.id))
                    return;
                const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
                elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector),
                typeof Cart < "u" && new Cart().renderContents(parsedState)
            }
            ),
            document.getElementById("Cart-Drawer") && document.getElementById("Cart-Drawer").open()
        }
        getSectionInnerHTML(html, selector=".shopify-section") {
            return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML
        }
    }
    customElements.define("product-card-small", ProductCardSmall)
}
class PanelClose extends HTMLElement {
    constructor() {
        super();
        let cc = document.querySelector(".click-capture");
        this.addEventListener("click", e => this.close_panel(e)),
        document.addEventListener("keyup", e => {
            e.code && e.code.toUpperCase() === "ESCAPE" && document.querySelectorAll(".side-panel").forEach(panel => {
                document.body.classList.contains("open-cc--product") ? (document.body.classList.remove("open-cc--product"),
                setTimeout( () => {
                    document.querySelector("#Product-Drawer-Content").innerHTML = ""
                }
                , 260)) : (panel.classList.contains("cart-drawer") && document.getElementById("cart-drawer-toggle").focus(),
                document.body.classList.remove("open-cc")),
                panel.classList.remove("active")
            }
            )
        }
        ),
        cc.addEventListener("click", e => {
            let panel = document.querySelector(".side-panel.active");
            panel && (panel.classList.remove("active"),
            document.body.classList.remove("open-cc"))
        }
        )
    }
    close_panel(e) {
        e.preventDefault(),
        e.target.closest(".side-panel").classList.remove("active"),
        document.body.classList.contains("open-cc--product") ? (document.body.classList.remove("open-cc--product"),
        setTimeout( () => {
            document.querySelector("#Product-Drawer-Content").innerHTML = ""
        }
        , 260)) : document.body.classList.remove("open-cc")
    }
}
if (customElements.define("side-panel-close", PanelClose),
!customElements.get("cart-drawer")) {
    class CartDrawer extends HTMLElement {
        constructor() {
            super()
        }
        connectedCallback() {
            document.getElementById("cart-drawer-toggle")?.addEventListener("click", e => {
                e.preventDefault(),
                document.body.classList.add("open-cc"),
                this.classList.add("active"),
                this.focus(),
                dispatchCustomEvent("cart-drawer:open")
            }
            ),
            this.debouncedOnChange = debounce(event => {
                this.onChange(event)
            }
            , 300),
            document.addEventListener("cart:refresh", event => {
                this.refresh()
            }
            ),
            this.addEventListener("change", this.debouncedOnChange.bind(this)),
            this.removeProductEvent()
        }
        onChange(event) {
            event.target.classList.contains("qty") && this.updateQuantity(event.target.dataset.index, event.target.value)
        }
        removeProductEvent() {
            this.querySelectorAll(".remove").forEach(remove => {
                remove.addEventListener("click", event => {
                    this.updateQuantity(event.target.dataset.index, "0"),
                    event.preventDefault()
                }
                )
            }
            )
        }
        getSectionsToRender() {
            return [{
                id: "Cart-Drawer",
                section: "cart-drawer",
                selector: ".cart-drawer"
            }, {
                id: "cart-drawer-toggle",
                section: "cart-bubble",
                selector: ".thb-item-count"
            }]
        }
        getSectionInnerHTML(html, selector) {
            return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML
        }
        updateQuantity(line, quantity) {
            this.querySelector(`#CartDrawerItem-${line}`).classList.add("thb-loading");
            const body = JSON.stringify({
                line,
                quantity,
                sections: this.getSectionsToRender().map(section => section.section),
                sections_url: window.location.pathname
            });
            dispatchCustomEvent("line-item:change:start", {
                quantity
            }),
            fetch(`${theme.routes.cart_change_url}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json"
                },
                body
            }).then(response => response.text()).then(state => {
                const parsedState = JSON.parse(state);
                this.getSectionsToRender().forEach(section => {
                    const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
                    parsedState.sections && (elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector))
                }
                ),
                this.removeProductEvent(),
                dispatchCustomEvent("line-item:change:end", {
                    quantity,
                    cart: parsedState
                }),
                this.querySelector(`#CartDrawerItem-${line}`)?.classList.remove("thb-loading")
            }
            )
        }
        open() {
            document.body.classList.add("open-cc"),
            this.classList.add("active"),
            dispatchCustomEvent("cart-drawer:open")
        }
        refresh() {
            fetch(`${window.location.pathname}?sections=cart-drawer,cart-bubble`).then(response => response.text()).then(state => {
                const parsedState = JSON.parse(state);
                this.getSectionsToRender().forEach(section => {
                    const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
                    elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState[section.section], section.selector)
                }
                ),
                this.removeProductEvent()
            }
            )
        }
    }
    customElements.define("cart-drawer", CartDrawer)
}
class FooterMenuToggle {
    constructor() {
        let _this = this;
        document.querySelectorAll(".thb-widget-title.collapsible").forEach(button => {
            button.addEventListener("click", e => {
                button.classList.toggle("active")
            }
            )
        }
        )
    }
}
if (!customElements.get("custom-select")) {
    class CustomSelect extends HTMLElement {
        constructor() {
            super(),
            this.detailsements = {
                form: this.closest("form"),
                input: this.querySelector("input"),
                button: this.querySelector(".custom-select--button"),
                panel: this.querySelector(".custom-select--list")
            },
            this.autoClose = this.dataset.autoClose,
            this.detailsements.button.addEventListener("click", this.toggleSelector.bind(this)),
            this.detailsements.button.addEventListener("focusout", this.closeSelector.bind(this)),
            this.addEventListener("keyup", this.onContainerKeyUp.bind(this)),
            this.detailsements.panel.querySelectorAll("button").forEach(item => item.addEventListener("click", this.onItemClick.bind(this)))
        }
        hidePanel() {
            this.detailsements.button.setAttribute("aria-expanded", "false"),
            this.classList.remove("custom-select--active")
        }
        onContainerKeyUp(event) {
            event.code.toUpperCase() === "ESCAPE" && (this.hidePanel(),
            this.detailsements.button.focus())
        }
        onItemClick(event) {
            event.preventDefault(),
            this.detailsements.input.value = event.currentTarget.dataset.value,
            this.detailsements.input.dispatchEvent(new Event("change")),
            this.detailsements.button.querySelector(".custom-select--text").textContent = event.currentTarget.textContent,
            this.detailsements.form && this.detailsements.form.dispatchEvent(new Event("input")),
            this.autoClose && this.classList.remove("custom-select--active")
        }
        toggleSelector(event) {
            event.preventDefault(),
            this.classList.toggle("custom-select--active"),
            this.classList.contains("custom-select--active") ? (this.detailsements.button.focus(),
            this.detailsements.button.setAttribute("aria-expanded", (this.detailsements.button.getAttribute("aria-expanded") === "false").toString())) : this.hidePanel()
        }
        closeSelector(event) {
            const shouldClose = event.relatedTarget && event.relatedTarget.classList.contains("custom-select--button");
            (event.relatedTarget === null || shouldClose) && this.hidePanel()
        }
    }
    customElements.define("custom-select", CustomSelect)
}
if (!customElements.get("localization-form")) {
    class LocalizationForm extends HTMLElement {
        constructor() {
            super(),
            this.form = this.querySelector("form"),
            this.inputs = this.form.querySelectorAll('input[name="locale_code"], input[name="country_code"]'),
            this.debouncedOnSubmit = debounce(event => {
                this.onSubmitHandler(event)
            }
            , 200),
            this.inputs.forEach(item => item.addEventListener("change", this.debouncedOnSubmit.bind(this)))
        }
        onSubmitHandler(event) {
            this.form && this.form.submit()
        }
    }
    customElements.define("localization-form", LocalizationForm)
}
if (!customElements.get("side-panel-content-tabs")) {
    class SidePanelContentTabs extends HTMLElement {
        constructor() {
            super(),
            this.buttons = this.querySelectorAll("button"),
            this.panels = this.parentElement.querySelectorAll(".side-panel-content--tab-panel")
        }
        connectedCallback() {
            this.setupButtonObservers()
        }
        disconnectedCallback() {}
        setupButtonObservers() {
            this.buttons.forEach( (item, i) => {
                item.addEventListener("click", e => {
                    this.toggleActiveClass(i),
                    e.preventDefault()
                }
                )
            }
            )
        }
        toggleActiveClass(i) {
            this.buttons.forEach(button => {
                button.classList.remove("tab-active")
            }
            ),
            this.buttons[i].classList.add("tab-active"),
            this.panels.forEach(panel => {
                panel.classList.remove("tab-active")
            }
            ),
            this.panels[i].classList.add("tab-active")
        }
    }
    customElements.define("side-panel-content-tabs", SidePanelContentTabs)
}
if (!customElements.get("collapsible-row")) {
    class CollapsibleRow extends HTMLElement {
        constructor() {
            super(),
            this.details = this.querySelector("details"),
            this.summary = this.querySelector("summary"),
            this.content = this.querySelector(".collapsible__content"),
            this.animation = null,
            this.isClosing = !1,
            this.isExpanding = !1
        }
        connectedCallback() {
            this.setListeners()
        }
        setListeners() {
            this.querySelector("summary").addEventListener("click", e => this.onClick(e))
        }
        instantClose() {
            this.tl.timeScale(10).reverse()
        }
        animateClose() {
            this.tl.timeScale(3).reverse()
        }
        animateOpen() {
            this.tl.timeScale(1).play()
        }
        onClick(e) {
            e.preventDefault(),
            this.details.style.overflow = "hidden",
            this.isClosing || !this.details.open ? this.open() : (this.isExpanding || this.details.open) && this.shrink()
        }
        shrink() {
            this.isClosing = !0;
            const startHeight = `${this.details.offsetHeight}px`
              , endHeight = `${this.summary.offsetHeight}px`;
            this.animation && this.animation.cancel(),
            this.animation = this.details.animate({
                height: [startHeight, endHeight]
            }, {
                duration: 250,
                easing: "ease"
            }),
            this.animation.onfinish = () => this.onAnimationFinish(!1),
            this.animation.oncancel = () => this.isClosing = !1
        }
        open() {
            this.details.style.height = `${this.details.offsetHeight}px`,
            this.details.open = !0,
            window.requestAnimationFrame( () => this.expand())
        }
        expand() {
            this.isExpanding = !0;
            const startHeight = `${this.details.offsetHeight}px`
              , endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;
            this.animation && this.animation.cancel(),
            this.animation = this.details.animate({
                height: [startHeight, endHeight]
            }, {
                duration: 400,
                easing: "ease-out"
            }),
            this.animation.onfinish = () => this.onAnimationFinish(!0),
            this.animation.oncancel = () => this.isExpanding = !1
        }
        onAnimationFinish(open) {
            this.details.open = open,
            this.animation = null,
            this.isClosing = !1,
            this.isExpanding = !1,
            this.details.style.height = this.details.style.overflow = ""
        }
    }
    customElements.define("collapsible-row", CollapsibleRow)
}
class ProductRecommendations extends HTMLElement {
    constructor() {
        super()
    }
    fetchProducts() {
        fetch(this.dataset.url).then(response => response.text()).then(text => {
            const html = document.createElement("div");
            html.innerHTML = text;
            const recommendations = html.querySelector("product-recommendations");
            recommendations && recommendations.innerHTML.trim().length && (this.innerHTML = recommendations.innerHTML),
            this.classList.add("product-recommendations--loaded")
        }
        ).catch(e => {
            console.error(e)
        }
        )
    }
    connectedCallback() {
        this.fetchProducts()
    }
}
if (customElements.define("product-recommendations", ProductRecommendations),
!customElements.get("quick-view")) {
    class QuickView extends HTMLElement {
        constructor() {
            super()
        }
        connectedCallback() {
            this.cc = document.querySelector(".click-capture--product"),
            this.drawer = document.getElementById("Product-Drawer"),
            this.body = document.body,
            this.addEventListener("click", this.setupEventListener.bind(this)),
            this.cc.addEventListener("click", this.setupCCEventListener.bind(this))
        }
        setupCCEventListener(e) {
            this.body.classList.remove("open-cc--product"),
            this.drawer.classList.remove("active"),
            setTimeout( () => {
                this.drawer.querySelector("#Product-Drawer-Content").innerHTML = ""
            }
            , 260)
        }
        setupEventListener(e) {
            e.preventDefault();
            let productHandle = this.dataset.productHandle
              , href = `${theme.routes.root_url}/products/${productHandle}?view=quick-view`;
            href = href.replace("//", "/"),
            !(!href || !productHandle) && (this.classList.contains("loading") || (this.classList.add("loading"),
            fetch(href, {
                method: "GET"
            }).then(response => (this.classList.remove("loading"),
            response.text())).then(text => {
                const sectionInnerHTML = new DOMParser().parseFromString(text, "text/html").querySelector("#Product-Drawer-Content").innerHTML;
                this.renderQuickview(sectionInnerHTML, href, productHandle)
            }
            )))
        }
        renderQuickview(sectionInnerHTML, href, productHandle) {
            if (sectionInnerHTML) {
                this.drawer.querySelector("#Product-Drawer-Content").innerHTML = sectionInnerHTML;
                let js_files = this.drawer.querySelector("#Product-Drawer-Content").querySelectorAll("script");
                if (js_files.length > 0) {
                    var head = document.getElementsByTagName("head")[0];
                    js_files.forEach( (js_file, i) => {
                        let script = document.createElement("script");
                        script.src = js_file.src,
                        head.appendChild(script)
                    }
                    )
                }
                setTimeout( () => {
                    Shopify && Shopify.PaymentButton && Shopify.PaymentButton.init(),
                    window.ProductModel && window.ProductModel.loadShopifyXR()
                }
                , 300),
                this.body.classList.add("open-cc--product"),
                this.drawer.classList.add("active"),
                this.drawer.querySelector(".side-panel-close").focus(),
                setTimeout( () => {
                    this.drawer.querySelector("#Product-Slider").reInit(),
                    window.dispatchEvent(new Event("resize"))
                }
                , 100),
                dispatchCustomEvent("quick-view:open", {
                    productUrl: href,
                    productHandle
                }),
                addIdToRecentlyViewed(productHandle)
            }
        }
    }
    customElements.define("quick-view", QuickView)
}
class AnimatedMarkers {
    constructor() {
        this.markers = document.querySelectorAll(".svg-marker path"),
        this.animations_enabled = document.body.classList.contains("animations-true") && typeof gsap < "u",
        this.animations_enabled && this.markers.length && this.setupEventListeners()
    }
    setupEventListeners() {
        this.markers.forEach( (marker, i) => {
            gsap.from(marker, {
                duration: 1,
                ease: "power4.inOut",
                drawSVG: "0%",
                scrollTrigger: {
                    trigger: marker,
                    start: "top 70%",
                    end: "bottom 80%"
                }
            })
        }
        )
    }
}
function addIdToRecentlyViewed(handle) {
    if (!handle) {
        let product = document.querySelector(".thb-product-detail");
        product && (handle = product.dataset.handle)
    }
    if (handle) {
        if (window.localStorage) {
            let recentIds = window.localStorage.getItem("recently-viewed");
            recentIds != "undefined" && recentIds != null && (window.recentlyViewedIds = JSON.parse(recentIds))
        }
        var i = window.recentlyViewedIds.indexOf(handle);
        i > -1 && window.recentlyViewedIds.splice(i, 1),
        window.recentlyViewedIds.unshift(handle),
        window.localStorage && window.localStorage.setItem("recently-viewed", JSON.stringify(window.recentlyViewedIds))
    }
}
document.addEventListener("DOMContentLoaded", () => {
    typeof AnimatedMarkers < "u" && new AnimatedMarkers,
    typeof FooterMenuToggle < "u" && new FooterMenuToggle
}
);
function openShopifyChat() {
    const chatEl = document.querySelector("#shopify-chat inbox-online-store-chat");
    if (!chatEl) {
        console.warn("Shopify Chat not loaded yet");
        return
    }
    const tryOpen = () => {
        const shadow = chatEl.shadowRoot;
        if (!shadow)
            return !1;
        const btn = shadow.querySelector("button");
        return btn ? (btn.click(),
        !0) : !1
    }
    ;
    if (!tryOpen()) {
        const interval = setInterval( () => {
            tryOpen() && clearInterval(interval)
        }
        , 300)
    }
}






if (typeof debounce === 'undefined') {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}
/**
 *  @class
 *  @function ThemeHeader
 */

if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    constructor() {
      super();

    }
    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = this.querySelector('.mobile-toggle-wrapper');

      document.addEventListener('keyup', (e) => {
        if (e.code) {
          if (e.code.toUpperCase() === 'ESCAPE') {
            this.toggle.removeAttribute('open');
            this.toggle.classList.remove('active');
          }
        }
      });
      if (this.classList.contains('header-sticky--active')) {
        document.body.classList.add('header-sticky--active');
      }
      this.toggle.querySelector('.mobile-toggle').addEventListener('click', (e) => {
        if (this.toggle.classList.contains('active')) {
          e.preventDefault();
          document.body.classList.remove('overflow-hidden');
          this.toggle.classList.remove('active');
          this.closeAnimation(this.toggle);
        } else {
          document.body.classList.add('overflow-hidden');
          setTimeout(() => {
            this.toggle.classList.add('active');
          });
        }
      });

      // Sticky Header Class
      window.addEventListener('scroll', this.setStickyClass.bind(this), {
        passive: true
      });

      // Mobile Menu offset
      window.addEventListener('scroll', this.setHeaderOffset.bind(this), {
        passive: true
      });
      window.addEventListener('scroll', this.setHeaderHeight.bind(this), {
        passive: true
      });

      window.dispatchEvent(new Event('scroll'));

      if (document.querySelector('.announcement-bar-section')) {

        window.addEventListener('scroll', this.setAnnouncementHeight(), {
          passive: true
        });
        window.dispatchEvent(new Event('resize'));
      }

      // Mobile Navigation transparent header support.
      setTimeout(() => {

        this.mobile_nav = document.querySelector('.header-mobile-navigation');

        if (this.mobile_nav && this.classList.contains('transparent--true')) {
          this.mobile_nav.classList.add('is-fixed');
        }
      }, 100);
      // Buttons.
      this.menu.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
      this.menu.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
    }
    setStickyClass() {
      if (this.classList.contains('header-sticky--active')) {
        let offset = parseInt(this.getBoundingClientRect().top, 10) + document.documentElement.scrollTop;
        this.classList.toggle('is-sticky', window.scrollY >= offset && window.scrollY > 0);
      }
    }
    setAnnouncementHeight() {
      const a_bar = document.querySelector('.announcement-bar-section');
      let h = a_bar.clientHeight;
      document.documentElement.style.setProperty('--announcement-height', h + 'px');
    }
    setHeaderOffset() {
      let h = this.header_section.getBoundingClientRect().top;
      document.documentElement.style.setProperty('--header-offset', h + 'px');
    }
    setHeaderHeight() {
      let h = this.clientHeight;
      document.documentElement.style.setProperty('--header-height', h + 'px');
    }
    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest('.link-container');
      const isOpen = detailsElement.hasAttribute('open');
      if (this.querySelector('.parent-link-back--button')) {
        this.menu.scrollTop = 0;
      }
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
      }, 100);
    }
    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }
  customElements.define('theme-header', ThemeHeader);
}

/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();

      this.submenus = this.querySelectorAll('.thb-full-menu>.menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu');
    }
    connectedCallback() {
      if (!this.submenus.length) {
        return;
      }
      const _this = this;
      // resize on initial load
      document.fonts.ready.then(function() {
        window.addEventListener('resize', debounce(function() {
          _this.resizeSubMenus();
        }, 100));
      });

    }
    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(':scope >.menu-item-has-children>.sub-menu');

        sub_submenus.forEach((sub_submenu) => {
          let w = sub_submenu.offsetWidth,
            l = sub_submenu.parentElement.getBoundingClientRect().left + sub_submenu.parentElement.offsetWidth,
            total = w + l;

          if (total > window.innerWidth) {
            sub_submenu.parentElement.classList.add('left-submenu');
          } else if (sub_submenu.parentElement.classList.contains('left-submenu')) {
            sub_submenu.parentElement.classList.remove('left-submenu');
          }
        });
      });
    }
  }
  customElements.define('full-menu', FullMenu);
}

